import { z } from 'zod';

import type { StatutoryTreatmentStatusResponse } from '../ap/statutory-treatment.js';
import { AccountType, type ApBillStatus, NaturalBalance } from '../generated/enums.js';

/** Shared expiration policy for every statutory preparation entry point. */
export const TAX_PREPARATION_TIMEOUT_MS = 120_000;

/** AI proposes attribution only. It cannot post journals or invent opening balances. */
export const TaxLiabilityResolutionProposalSchema = z.object({
  decisions: z.array(
    z.object({
      journalLineId: z.string(),
      slotId: z.string(),
      assessmentPeriodYear: z.number().int(),
      assessmentPeriodMonth: z.number().int(),
      amount: z.string(),
      evidenceQuote: z.string(),
      reason: z.string(),
    }),
  ),
  unresolved: z.array(z.object({ journalLineId: z.string().nullable(), reason: z.string() })),
});
export type TaxLiabilityResolutionProposal = z.infer<typeof TaxLiabilityResolutionProposalSchema>;
export type TaxLiabilityResolutionStatus =
  'queued' | 'running' | 'ready' | 'needs_review' | 'failed';

/** Concrete accounting change displayed before any account or mapping mutation. */
export const TaxAccountChangeProposalSchema = z.object({
  action: z.enum(['MAP_EXISTING_ACCOUNT', 'CREATE_ACCOUNT']),
  /** Liability rails need migration; ordinary debit slots use the fiscal mapping write path. */
  mappingKind: z.enum(['TAX_LIABILITY_RAIL', 'TAX_ACCOUNT']).optional(),
  versionId: z.string(),
  slotCode: z.string(),
  slotName: z.string(),
  accountId: z.string().nullable(),
  accountCode: z.string(),
  accountName: z.string(),
  adoptionFingerprint: z.string().nullable(),
  purpose: z.string(),
  reason: z.string(),
  scope: z.object({
    entityId: z.string(),
    entityName: z.string(),
    versionId: z.string(),
    versionName: z.string(),
    bookIds: z.array(z.string()),
    sharedAcrossEntities: z.boolean(),
    affectedBills: z.number().int().nonnegative(),
  }),
  parentAccountId: z.string().nullable().optional(),
  parentAccountCode: z.string().nullable().optional(),
  parentAccountName: z.string().nullable().optional(),
  /** Existing displayed suggestion whose exact parent authorizes this user-selected creation. */
  sourceAccountId: z.string().nullable().optional(),
  /** Present and true only after a human replaces the AI suggestion in the staging dialog. */
  selectedByUser: z.boolean().optional(),
  accountType: z.enum(AccountType).nullable().optional(),
  naturalBalance: z.enum(NaturalBalance).nullable().optional(),
});
export type TaxAccountChangeProposal = z.infer<typeof TaxAccountChangeProposalSchema>;

/** A human-owned tax decision remains unchanged until this exact suggestion is accepted. */
export const TaxTreatmentSuggestionSchema = z.object({
  suggestionId: z.string(),
  taxLineId: z.string(),
  currentSlotCode: z.string().nullable(),
  suggestedSlotCode: z.string().nullable(),
  suggestedTreatment: z.literal('CLASSIFY').nullable(),
  classificationAccount: z
    .object({
      versionId: z.string(),
      accountId: z.string(),
      code: z.string(),
      name: z.string(),
      purpose: z.string(),
    })
    .nullable(),
  evidenceQuote: z.string(),
  reason: z.string(),
  fingerprint: z.string(),
});
export type TaxTreatmentSuggestion = z.infer<typeof TaxTreatmentSuggestionSchema>;

export interface TaxLiabilityResolutionResponse {
  runId: string;
  billId: string;
  status: TaxLiabilityResolutionStatus;
  reviewRequestId: string;
  createdAt?: string;
  /** Persisted per evidence revision; explicit retry begins a new retry budget. */
  automaticReplacementCount?: number;
  configurationCommitted?: boolean;
  /** The bill evidence changed; refresh preparation before confirming any saved decision. */
  requiresReanalysis?: boolean;
  updatedAt: string;
  accountProposals?: TaxAccountChangeProposal[];
  accountProposalFingerprint?: string | null;
  taxSuggestions?: TaxTreatmentSuggestion[];
  dismissedTaxSuggestions?: TaxTreatmentSuggestion[];
  continuation?: 'accept' | 'defer_review' | null;
  continuationDetail?: string | null;
  /**
   * Set when accepting the continuation would approve a CLASSIFY the system derived (not a person's
   * override): the person must see who the guia's taxpayer is before it posts as an expense.
   */
  derivedClassifyConfirmation?: DerivedClassifyConfirmation | null;
  /** The statutory blocker that leaves no continuation, so the person sees what is missing. */
  continuationBlock?: StatutoryContinuationBlock | null;
  pendingIdentification?: {
    lineCount: number;
    taxCodes: string[];
    reason: 'ACTIVE_DEBT_ORIGIN_REQUIRED' | 'IDENTITY_REQUIRED';
  } | null;
  proposalFingerprint?: string | null;
  mappingSummary?: {
    taxLineId: string;
    taxCode: string | null;
    slotCode: string;
    slotName: string;
    accountCode: string | null;
    accountName: string | null;
    reason: string;
  }[];
}
/**
 * The body of a `TAX_LIABILITY_RAIL_MAPPING_CONFLICT` refusal: the slot's rail already exists, so
 * only its parent or legado account may be chosen. The web writes the user's text from these
 * fields; `message` is an English fallback. The account fields are absent when the rail could
 * not be read back (then `nextAction` is `REFRESH_ANALYSIS`).
 */
export interface TaxLiabilityRailMappingConflictDetails {
  code: 'TAX_LIABILITY_RAIL_MAPPING_CONFLICT';
  message: string;
  nextAction: 'SELECT_RAIL_ACCOUNT' | 'REFRESH_ANALYSIS';
  slotCode?: string;
  slotName?: string;
  glAccountId?: string;
  accountCode?: string;
  accountName?: string;
  legadoAccountId?: string | null;
  legadoAccountCode?: string | null;
  legadoAccountName?: string | null;
}

export interface TaxLiabilityResolutionAuditResponse {
  run: TaxLiabilityResolutionResponse;
  events: { eventId: string; eventType: string; timestamp: string; details: unknown }[];
}

export const StatutoryBillActionIntentSchema = z.object({
  actionId: z.uuid(),
  action: z.enum(['SUBMIT', 'APPROVE', 'RETRY']),
  stepInstanceId: z.uuid().optional(),
  comment: z.string().max(4000).optional(),
});
export type StatutoryBillActionIntent = z.infer<typeof StatutoryBillActionIntentSchema>;

export const ExecuteStatutoryBillActionSchema = z.object({
  intent: StatutoryBillActionIntentSchema,
  planFingerprint: z.string(),
  runId: z.uuid().optional(),
  accountProposalFingerprint: z.string().optional(),
  accountChoices: z
    .array(
      z.object({
        versionId: z.uuid(),
        slotCode: z.string(),
        choice: z.enum(['MAP_EXISTING_ACCOUNT', 'CREATE_ACCOUNT']),
        accountId: z.uuid().optional(),
      }),
    )
    .default([]),
  taxChoices: z
    .array(
      z.object({
        suggestionId: z.string(),
        fingerprint: z.string(),
        decision: z.enum(['accept', 'dismiss']),
      }),
    )
    .default([]),
  markForAccountingReview: z.boolean().default(true),
  acknowledgedWarnings: z.array(z.string()).default([]),
  selectTemporaryAccounting: z.boolean().default(false),
});
export type ExecuteStatutoryBillActionRequest = z.infer<typeof ExecuteStatutoryBillActionSchema>;

/** The statutory blocker behind an analysis that grants no continuation. */
export type StatutoryContinuationBlock = {
  blockCode: NonNullable<StatutoryTreatmentStatusResponse['blockCode']>;
} & {
  [K in 'blockedSlotCode' | 'slotCode' | 'payerEntityId' | 'payerTaxId']: NonNullable<
    StatutoryTreatmentStatusResponse[K]
  > | null;
};

/** Who the guia's taxpayer is when the system, not a person, decided to expense it. */
export interface DerivedClassifyConfirmation {
  payerMatch: string | null;
  payerName: string | null;
  payerTaxId: string | null;
}

/** Codes the statutory bill action itself returns; other codes come from validation or posting. */
export const STATUTORY_BILL_ACTION_CODES = {
  APPROVAL_PERMISSION_REQUIRED: 'APPROVAL_PERMISSION_REQUIRED',
  APPROVAL_CONSENT_REQUIRED: 'APPROVAL_CONSENT_REQUIRED',
  APPROVAL_NOT_COMPLETED: 'APPROVAL_NOT_COMPLETED',
  ANALYSIS_STALE: 'STATUTORY_ANALYSIS_STALE',
  ANALYSIS_INCONCLUSIVE: 'STATUTORY_ANALYSIS_INCONCLUSIVE',
  ANALYSIS_FAILED: 'STATUTORY_ANALYSIS_FAILED',
  PLAN_CHANGED: 'STATUTORY_PLAN_CHANGED',
  BILL_VALIDATION_REQUIRED: 'BILL_VALIDATION_REQUIRED',
  ACTION_FAILED: 'STATUTORY_ACTION_FAILED',
  /** Warning: the person confirms a CLASSIFY the system derived before it is approved. */
  DERIVED_CLASSIFY_CONFIRMATION: 'STATUTORY_DERIVED_CLASSIFY_CONFIRMATION',
} as const;
export type StatutoryBillActionCode =
  (typeof STATUTORY_BILL_ACTION_CODES)[keyof typeof STATUTORY_BILL_ACTION_CODES];

export interface StatutoryBillActionIssue {
  phase: 'VALIDATING_PLAN' | 'APPLYING_CONFIGURATION' | 'EXECUTING_BILL_ACTION';
  /** A {@link StatutoryBillActionCode}, or the code of the validation or posting error. */
  code: string;
  message: string;
  /** The bill validation errors behind a validation block, so the web can word each one. */
  validationErrors?: { field: string; code?: string; message: string }[];
  nextAction: 'RETRY' | 'REVIEW_DECISIONS' | 'REPAIR_PAYMENT' | 'REQUEST_PERMISSION' | 'EDIT_BILL';
  retryable: boolean;
  requiredCapability?: string;
}
export interface StatutoryBillActionResponse {
  actionId: string;
  state:
    | 'WAITING_ANALYSIS'
    | 'DECISIONS_REQUIRED'
    | 'READY'
    | 'BLOCKED'
    | 'SUBMITTED'
    | 'STEP_ACCEPTED'
    | 'APPROVED';
  billStatus: ApBillStatus;
  postingFingerprint: string;
  planFingerprint: string;
  resolution: TaxLiabilityResolutionResponse | null;
  /** `field` and `details` let the web word a readiness warning from its code. */
  warnings: {
    code: string;
    message: string;
    field?: string;
    details?: Record<string, unknown>;
  }[];
  configurationCommitted: boolean;
  issue: StatutoryBillActionIssue | null;
}
